import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import ApiService from "../../services/apiService";
import Loader from "../../elements/Loader";

const DEFAULT_TIME = "09:00";

export default function AddFestivalCampaign() {
    const navigate = useNavigate();

    const [loading, setLoading]       = useState(false);
    const [pujaOptions, setPujaOptions] = useState([]);
    const [loadingPujas, setLoadingPujas] = useState(false);

    // Form state
    const [festivalName, setFestivalName]   = useState("");
    const [selectedPuja, setSelectedPuja]   = useState(null);
    const [scheduledDate, setScheduledDate] = useState("");
    const [scheduledTime, setScheduledTime] = useState(DEFAULT_TIME);
    const [notes, setNotes]                 = useState("");

    // Fetch puja list for the dropdown
    useEffect(() => {
        setLoadingPujas(true);
        ApiService.get("service/list")
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                setPujaOptions(
                    list.map(s => ({ value: s.id, label: s.title || s.name || `Puja #${s.id}` }))
                );
            })
            .catch(() => toast.error("Could not load puja list"))
            .finally(() => setLoadingPujas(false));
    }, []);

    const validate = () => {
        if (!festivalName.trim()) { toast.error("Festival name is required"); return false; }
        if (!selectedPuja)        { toast.error("Please select a puja");       return false; }
        if (!scheduledDate)       { toast.error("Scheduled date is required");  return false; }
        if (!scheduledTime)       { toast.error("Scheduled time is required");  return false; }
        // Prevent past dates
        const chosen = new Date(`${scheduledDate}T${scheduledTime}:00+05:30`);
        if (chosen < new Date()) { toast.error("Scheduled date & time must be in the future"); return false; }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            await ApiService.post("festival-campaign", {
                festivalName: festivalName.trim(),
                pujaId:       selectedPuja.value,
                pujaName:     selectedPuja.label,
                scheduledDate,
                scheduledTime,
                notes: notes.trim() || undefined
            });
            toast.success("Festival campaign added successfully");
            navigate("/festival-campaign");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to create campaign");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="row">
            {loading && <Loader />}

            <div className="col-xl-8 col-lg-12 offset-xl-2">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="card-title mb-0">Add Festival Puja Campaign</h4>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => navigate("/festival-campaign")}
                        >
                            ← Back
                        </button>
                    </div>

                    <div className="card-body">
                        <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                            The TeleCRM webhook will be triggered for <strong>all leads</strong> on the selected
                            date and time (IST). The cron checks every minute.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Festival Name */}
                                <div className="mb-3 col-md-6">
                                    <label className="form-label">
                                        Festival Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. Navratri 2026, Diwali"
                                        value={festivalName}
                                        onChange={e => setFestivalName(e.target.value)}
                                    />
                                </div>

                                {/* Puja Picker */}
                                <div className="mb-3 col-md-6">
                                    <label className="form-label">
                                        Select Puja <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        options={pujaOptions}
                                        value={selectedPuja}
                                        onChange={setSelectedPuja}
                                        isLoading={loadingPujas}
                                        isSearchable
                                        placeholder="Search and select a puja..."
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: base => ({ ...base, minHeight: 42 }),
                                            menu:    base => ({ ...base, zIndex: 9999 })
                                        }}
                                    />
                                </div>

                                {/* Scheduled Date */}
                                <div className="mb-3 col-md-6">
                                    <label className="form-label">
                                        Scheduled Date (IST) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={scheduledDate}
                                        min={new Date().toISOString().split("T")[0]}
                                        onChange={e => setScheduledDate(e.target.value)}
                                    />
                                </div>

                                {/* Scheduled Time */}
                                <div className="mb-3 col-md-6">
                                    <label className="form-label">
                                        Scheduled Time (IST) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={scheduledTime}
                                        onChange={e => setScheduledTime(e.target.value)}
                                    />
                                    <small className="text-muted">
                                        Cron fires every minute — the webhook triggers at this exact HH:MM.
                                    </small>
                                </div>

                                {/* Notes */}
                                <div className="mb-4 col-md-12">
                                    <label className="form-label">Notes (optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        placeholder="Any internal notes about this campaign..."
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Summary card */}
                            {festivalName && selectedPuja && scheduledDate && scheduledTime && (
                                <div className="alert alert-info mb-4" style={{ fontSize: 13 }}>
                                    <strong>Summary:</strong> On <strong>{scheduledDate}</strong> at{" "}
                                    <strong>{scheduledTime} IST</strong>, the TeleCRM webhook{" "}
                                    <code>festival_puja_campaign</code> will be triggered for all leads with
                                    festival <em>{festivalName}</em> and puja <em>{selectedPuja.label}</em>.
                                </div>
                            )}

                            <div className="d-flex gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save Campaign"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate("/festival-campaign")}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
